import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, Lock, Shield, Info } from 'lucide-react';
import api from '../lib/api';

export const AdminSetupPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [setupKey, setSetupKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [setupStatus, setSetupStatus] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        checkSetupStatus();
    }, []);

    const checkSetupStatus = async () => {
        try {
            const response = await api.get('/admin/setup/status');
            setSetupStatus(response.data);
        } catch (err) {
            console.error('Failed to check setup status:', err);
        } finally {
            setCheckingStatus(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await api.post('/admin/setup', {
                email,
                password,
                setup_key: setupKey
            });
            setResult(response.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Setup failed');
        } finally {
            setLoading(false);
        }
    };

    if (checkingStatus) {
        return (
            <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-64px)] flex items-center justify-center p-8" data-testid="admin-setup-page">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-accent" />
                        Admin Setup
                    </CardTitle>
                    <CardDescription>
                        One-time setup for admin account with full video access
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {/* Setup status alert */}
                    {setupStatus && !setupStatus.setup_available && !result && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Admin setup is currently disabled. Contact system administrator.
                            </AlertDescription>
                        </Alert>
                    )}

                    {setupStatus?.admin_exists && !result && (
                        <Alert className="mb-4 border-amber-200 bg-amber-50">
                            <Info className="h-4 w-4 text-amber-600" />
                            <AlertDescription className="text-amber-800">
                                An admin account already exists. This will update credentials if the email matches.
                            </AlertDescription>
                        </Alert>
                    )}

                    {result ? (
                        <div className="text-center py-4">
                            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                            <h3 className="font-semibold text-lg mb-2">Setup Complete!</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Admin account ready for {result.message?.split(' ').pop()}
                            </p>
                            <div className="text-left bg-secondary/50 rounded p-4 text-sm space-y-1">
                                <p><strong>User ID:</strong> <span className="font-mono text-xs">{result.user_id}</span></p>
                                <p><strong>Is Admin:</strong> <span className="text-green-600">Yes</span></p>
                                <p><strong>Total Videos:</strong> {result.total_videos}</p>
                                <p><strong>Entitlements:</strong> {result.total_entitlements}</p>
                                {result.new_entitlements_created > 0 && (
                                    <p><strong>New Entitlements:</strong> +{result.new_entitlements_created}</p>
                                )}
                            </div>
                            <Button 
                                className="mt-4 w-full bg-accent text-accent-foreground hover:bg-accent/90"
                                onClick={() => window.location.href = '/login'}
                            >
                                Go to Login
                            </Button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="text-sm font-medium mb-1 block">Admin Email</label>
                                <Input
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="admin-email-input"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Password</label>
                                <Input
                                    type="password"
                                    placeholder="Choose a secure password (min 8 chars)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    data-testid="admin-password-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Minimum 8 characters required
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium mb-1 block">Setup Key</label>
                                <Input
                                    type="password"
                                    placeholder="Enter setup key"
                                    value={setupKey}
                                    onChange={(e) => setSetupKey(e.target.value)}
                                    required
                                    data-testid="setup-key-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                    Security key from ADMIN_SETUP_KEY environment variable
                                </p>
                            </div>

                            {error && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}

                            <Button
                                type="submit"
                                className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                                disabled={loading || (setupStatus && !setupStatus.setup_available)}
                                data-testid="admin-setup-submit"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Setting up...
                                    </>
                                ) : (
                                    <>
                                        <Lock className="mr-2 h-4 w-4" />
                                        Setup Admin Account
                                    </>
                                )}
                            </Button>

                            {/* Security info */}
                            <div className="text-xs text-muted-foreground bg-secondary/30 rounded p-3 mt-4">
                                <p className="font-medium mb-1">Security Information:</p>
                                <ul className="list-disc list-inside space-y-0.5">
                                    <li>Rate limited to 5 attempts per 30 minutes</li>
                                    <li>All setup attempts are logged</li>
                                    <li>Can be disabled via environment variable</li>
                                </ul>
                            </div>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminSetupPage;
